import { SelectedItem } from "abreact/incubator/ui/datagrid/DataGridSelectionMachine";
import { ClassUtils } from "abweb/utils/ClassUtils";
/**
 * static helper utility class to assist in finer-grained control of the DataGridCellMeasurementFreshness in the context
 * of its use in the DataGrid.
 * Note: 8/17/23 In the future with the new implementation of DataGridCellMeasurementFreshness, I think we could remove the need for the DataGrid arg
 * in these methods, and maybe even eliminate this utility class and move the functionality into DataGridCellMeasurementFreshness itself. Not now though.
 */
export class CellMeasurerCacheUtils {
  /**
   * Clears an explicit set of items from the CellMeasurerCache.
   * @param {(SelectedItem | RowId)[] | null} items Note that null clears the whole cache.
   * Passing a RowId by itself (not a SelectedItem) is interpreted to mean clear all items at that RowId to the end.
   * @param {CellMeasurerCacheType | null} cellMeasurerCache
   * @param {DataGrid} dataGrid
   */
  static clearItems(items, cellMeasurerCache, dataGrid) {
    if (cellMeasurerCache == null) {
      return;
    }
    if (items == null) {
      cellMeasurerCache.clearAll();
      return;
    }
    for (let item of items) {
      if (ClassUtils.isInstanceOf(item, SelectedItem)) {
        // We have an explicit item to work with
        let selectedItem = item;
        if (selectedItem.isRow) {
          // For a row item, we need to clear all cells in that row
          CellMeasurerCacheUtils.clearRow(selectedItem.rowId, cellMeasurerCache, dataGrid);
        } else if (selectedItem.isCell) {
          CellMeasurerCacheUtils.clearCell(selectedItem.rowId, selectedItem.colId, cellMeasurerCache, dataGrid);
        } else if (selectedItem.isColumn) {
          CellMeasurerCacheUtils.clearColumn(selectedItem.colId, cellMeasurerCache, dataGrid);
        }
      } else {
        // Assume a RowId was passed
        // This is interpreted to mean clear all cellMeasurerCache items from the indicated RowId on down
        CellMeasurerCacheUtils.clearRowToEnd(item, cellMeasurerCache, dataGrid);
      }
    }
  }
  static clearRow(rowId, cellMeasurerCache, dataGrid) {
    let rowIndexOffset = dataGrid.props.showColumnHeaders ? dataGrid.fixedRowCount : 0;
    let rowIndex = dataGrid.props.rowIdToIndexMapper ? dataGrid.props.rowIdToIndexMapper(rowId) : rowId;
    rowIndex += rowIndexOffset;
    let colIndexOffset = dataGrid.props.showRowHeaderColumn ? 1 : 0;
    let firstColIndex = colIndexOffset;
    let lastColIndex = colIndexOffset + dataGrid.visibleLeafColumns.length - 1;
    for (let colIndex = firstColIndex; colIndex <= lastColIndex; colIndex++) {
      cellMeasurerCache.clear(rowIndex, colIndex);
    }
  }
  static clearRowToEnd(rowId, cellMeasurerCache, dataGrid) {
    let rowIndexOffset = dataGrid.props.showColumnHeaders ? dataGrid.fixedRowCount : 0;
    let rowIndexStart = dataGrid.props.rowIdToIndexMapper ? dataGrid.props.rowIdToIndexMapper(rowId) : rowId;
    if (rowIndexStart === 0) {
      // Optimization which is equivalent to clearAll()
      cellMeasurerCache.clearAll();
      return;
    }
    rowIndexStart += rowIndexOffset;
    cellMeasurerCache.clearRowToEnd(rowIndexStart);
  }
  static clearCell(rowId, colId, cellMeasurerCache, dataGrid) {
    let rowIndexOffset = dataGrid.props.showColumnHeaders ? dataGrid.fixedRowCount : 0;
    let rowIndex = dataGrid.props.rowIdToIndexMapper ? dataGrid.props.rowIdToIndexMapper(rowId) : rowId;
    rowIndex += rowIndexOffset;
    let colIndexOffset = dataGrid.props.showRowHeaderColumn ? 1 : 0;
    let visibleColIndex = dataGrid.getColumnIndexFromId(colId);
    if (visibleColIndex < 0) {
      return;
    }
    cellMeasurerCache.clear(rowIndex, colIndexOffset + visibleColIndex);
  }
  static clearColumn(colId, cellMeasurerCache, dataGrid) {
    let rowIndexOffset = dataGrid.props.showColumnHeaders ? dataGrid.fixedRowCount : 0;
    let colIndexOffset = dataGrid.props.showRowHeaderColumn ? 1 : 0;
    let visibleColIndex = dataGrid.getColumnIndexFromId(colId);
    if (visibleColIndex < 0) {
      return;
    }
    let colIndex = colIndexOffset + visibleColIndex;

    // Need to clear the cell cache value for all rowIndex values in the cache
    let cacheRowCount = cellMeasurerCache.rowCount;
    for (let rowIndex = rowIndexOffset; rowIndex <= cacheRowCount - rowIndexOffset; rowIndex++) {
      cellMeasurerCache.clear(rowIndex, colIndex);
    }
  }
}
//# sourceMappingURL=CellMeasurerCacheUtils.js.map
